import type { FC, ReactNode } from 'react'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown } from '@untitled-ui/icons-react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Collapse from '@mui/material/Collapse'
import SvgIcon from '@mui/material/SvgIcon'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { RouterLink } from '@/components/RouterLink'
import { useSideNavExpandedLayout } from '@/contexts/side-nav-expanded-context'
import { useAuth } from '@/hooks/use-auth'
import { paths } from '@/paths'
import { densityPx, densityPxStr } from '@/theme/base/density'
import {
  SIDE_NAV_ITEM_FONT_SIZE,
  SIDE_NAV_SUB_ITEM_FONT_SIZE,
  sideNavActiveButtonHoverSx,
  sideNavButtonHoverSx,
} from './side-nav-layout-dimensions'

interface SideNavItemProps {
  active?: boolean
  children?: ReactNode
  depth?: number
  disabled?: boolean
  external?: boolean
  firstChildPath?: string
  icon?: ReactNode
  label?: ReactNode
  badgeLabel?: number
  open?: boolean
  path?: string
  title: string
  isFolded?: boolean
  labelsVisible?: boolean
}

/** Aligné sur NavItem (réf.) : transition fluide tant que le nœud reste monté. */
const sideNavLabelMotionSx = (labelsShown: boolean) => ({
  overflow: 'hidden',
  whiteSpace: 'nowrap' as const,
  marginLeft: densityPxStr(3),
  opacity: labelsShown ? 1 : 0,
  transform: labelsShown ? 'translateX(0)' : `translateX(-${densityPx(10)}px)`,
  transition: 'opacity 0.3s ease, transform 0.3s ease',
  pointerEvents: labelsShown ? ('auto' as const) : ('none' as const),
})

/** Marge gauche (px) des icônes des entrées depth 0 — réutilisée pour Nouveau dossier / Compte. */
export const SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX = densityPx(10)

const SIDE_NAV_ICON_SIZE_PX = densityPx(28)
const SUB_NAV_BORDER_WIDTH_PX = 2

/** Alignement de la barre verticale des sous-entrées sous le centre de l’icône parent. */
export const SUB_NAV_GUIDE_OFFSET_PX =
  SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX +
  SIDE_NAV_ICON_SIZE_PX / 2 -
  SUB_NAV_BORDER_WIDTH_PX / 2

const sideNavFadeSiblingSx = (labelsShown: boolean) => ({
  opacity: labelsShown ? 1 : 0,
  transform: labelsShown ? 'translateX(0)' : `translateX(-${densityPx(10)}px)`,
  transition: 'opacity 0.3s ease, transform 0.3s ease',
  pointerEvents: labelsShown ? ('auto' as const) : ('none' as const),
})

const BranchChevron = ({
  open,
  labelsVisible,
}: {
  open: boolean
  labelsVisible: boolean
}) => (
  <Box
    aria-hidden
    component="span"
    data-testid="side-nav-branch-chevron"
    sx={{
      alignItems: 'center',
      color: 'var(--nav-item-icon-color)',
      display: 'flex',
      flex: '0 0 auto',
      ...sideNavFadeSiblingSx(labelsVisible),
    }}
  >
    <SvgIcon
      sx={{
        fontSize: densityPx(16),
        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
        transition: 'transform 0.2s ease',
      }}
    >
      <ChevronDown />
    </SvgIcon>
  </Box>
)

const depth0ItemButtonSx = ({
  active,
  isFolded,
  offset,
}: {
  active?: boolean
  isFolded?: boolean
  offset: number
}) => ({
  alignItems: 'center',
  borderLeft: `${densityPx(3)}px solid transparent`,
  borderRadius: 1,
  display: 'flex',
  gap: !isFolded ? 1 : 0,
  justifyContent: 'flex-start',
  pl: `${SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX - densityPx(3) + offset}px`,
  pr: densityPxStr(16),
  height: densityPxStr(40),
  textAlign: 'left' as const,
  width: '100%',
  ...(active && {
    borderLeftColor: 'var(--nav-item-icon-active-color)',
    backgroundColor: 'var(--nav-item-active-bg)',
  }),
  '&:hover': active ? sideNavActiveButtonHoverSx : sideNavButtonHoverSx,
})

const subNavItemButtonSx = ({ active }: { active?: boolean }) => ({
  alignItems: 'center',
  borderLeft: `${SUB_NAV_BORDER_WIDTH_PX}px solid`,
  borderLeftColor: active ? 'primary.main' : 'var(--nav-item-color)',
  borderRadius: 0,
  display: 'flex',
  gap: 1,
  justifyContent: 'flex-start',
  pl: densityPxStr(12),
  pr: densityPxStr(16),
  height: densityPxStr(40),
  textAlign: 'left' as const,
  width: '100%',
  '&:hover': active ? sideNavActiveButtonHoverSx : sideNavButtonHoverSx,
})

export const SideNavItem: FC<SideNavItemProps> = (props) => {
  const {
    active,
    children,
    depth = 0,
    disabled,
    external,
    firstChildPath,
    icon,
    label,
    badgeLabel,
    open: openProp,
    path,
    title,
    isFolded,
    labelsVisible = true,
  } = props
  const [open, setOpen] = useState<boolean>(!!openProp)
  const { user } = useAuth()
  const navigate = useNavigate()
  const { setSideNavExpanded } = useSideNavExpandedLayout()

  const handleBranchClick = useCallback((): void => {
    if (open) {
      setOpen(false)
      return
    }
    setOpen(true)
    setSideNavExpanded(true)
    if (firstChildPath) {
      navigate(firstChildPath)
    }
  }, [firstChildPath, navigate, open, setSideNavExpanded])

  const handleFoldedBranchClick = useCallback((): void => {
    setOpen(true)
    setSideNavExpanded(true)
    if (firstChildPath) {
      navigate(firstChildPath)
    }
  }, [firstChildPath, navigate, setSideNavExpanded])

  const offset = depth === 0 ? 0 : (depth - 1) * densityPx(16)
  const startIcon = depth === 0 ? icon : null

  // Branch

  if (children) {
    if (isFolded) {
      return (
        <li data-testid="side-nav-item-container-children">
          <Tooltip
            disableHoverListener={false}
            enterDelay={300}
            placement="right"
            title={title}
          >
            <ButtonBase
              disabled={disabled}
              onClick={handleFoldedBranchClick}
              sx={depth0ItemButtonSx({ active, isFolded, offset: 0 })}
            >
              {icon && (
                <Box
                  component="span"
                  sx={{
                    alignItems: 'center',
                    color: 'var(--nav-item-icon-color)',
                    display: 'inline-flex',
                    flex: '0 0 auto',
                    height: densityPxStr(28),
                    justifyContent: 'center',
                    ...(active && {
                      color: 'var(--nav-item-icon-active-color)',
                    }),
                  }}
                >
                  {icon}
                </Box>
              )}
            </ButtonBase>
          </Tooltip>
        </li>
      )
    }

    return (
      <li data-testid="side-nav-item-container-children">
        <ButtonBase
          disabled={disabled}
          onClick={handleBranchClick}
          title={title}
          sx={{
            ...depth0ItemButtonSx({ active, isFolded, offset }),
            gap: 1,
          }}
        >
          {startIcon && (
            <Box
              component="span"
              sx={{
                alignItems: 'center',
                color: 'var(--nav-item-icon-color)',
                display: 'flex',
                flex: '0 0 auto',
                height: densityPxStr(28),
                justifyContent: 'center',
                ...(active && {
                  color: 'var(--nav-item-icon-active-color)',
                }),
              }}
            >
              {startIcon}
            </Box>
          )}
          <Typography
            sx={{
              color: 'var(--nav-item-color)',
              flexGrow: 1,
              fontFamily: (theme) => theme.typography.fontFamily,
              fontSize: SIDE_NAV_ITEM_FONT_SIZE,
              fontWeight: active ? 600 : 300,
              lineHeight: densityPxStr(28),
              minWidth: 0,
              ...sideNavLabelMotionSx(labelsVisible),
              ...(active && {
                color: 'var(--nav-item-active-color)',
              }),
              ...(disabled && {
                color: 'var(--nav-item-disabled-color)',
              }),
            }}
          >
            {title}
          </Typography>
          <BranchChevron open={open} labelsVisible={labelsVisible} />
        </ButtonBase>

        <Collapse in={open} sx={{ mt: open ? 0.5 : 0 }}>
          <Box sx={{ ml: `${SUB_NAV_GUIDE_OFFSET_PX}px` }}>{children}</Box>
        </Collapse>
      </li>
    )
  }

  // Leaf

  const linkProps = path
    ? external
      ? {
          component: 'a',
          href: path,
          target: '_blank',
        }
      : {
          component: RouterLink,
          href: path,
        }
    : {}

  const linkButton = (
    <ButtonBase
      disabled={disabled}
      sx={
        depth === 0
          ? depth0ItemButtonSx({ active, isFolded, offset })
          : subNavItemButtonSx({ active })
      }
      {...linkProps}
    >
      {startIcon && (
        <Box
          component="span"
          sx={{
            alignItems: 'center',
            color: 'var(--nav-item-icon-color)',
            display: 'inline-flex',
            flex: '0 0 auto',
            height: densityPxStr(28),
            justifyContent: 'center',
            ...(active && {
              color: 'var(--nav-item-icon-active-color)',
            }),
          }}
        >
          {startIcon}
        </Box>
      )}
      {!isFolded && (
        <>
          <Typography
            sx={{
              color: 'var(--nav-item-color)',
              flexGrow: 1,
              fontFamily: (theme) => theme.typography.fontFamily,
              fontSize: depth > 0 ? SIDE_NAV_SUB_ITEM_FONT_SIZE : SIDE_NAV_ITEM_FONT_SIZE,
              fontWeight: active ? 500 : depth > 0 ? 300 : 300,
              lineHeight: title === 'Account' ? undefined : densityPxStr(28),
              minWidth: 0,
              ...sideNavLabelMotionSx(labelsVisible),
              ...(active && {
                color: 'var(--nav-item-active-color)',
              }),
              ...(disabled && {
                color: 'var(--nav-item-disabled-color)',
              }),
            }}
          >
            <Box component="span" display="block">
              {title}
            </Box>
            {path === paths.dashboard.account && (
              <Box
                component="span"
                sx={{
                  display: 'block',
                  fontSize: '0.8em',
                  lineHeight: 1,
                  color: 'var(--nav-section-title-color)',
                }}
              >
                {user?.email}
              </Box>
            )}
          </Typography>
          {label ? (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                flex: '0 0 auto',
                ...sideNavFadeSiblingSx(labelsVisible),
              }}
            >
              {label}
            </Box>
          ) : null}
        </>
      )}
    </ButtonBase>
  )

  return (
    <li data-testid="side-nav-item-container">
      <Tooltip
        disableHoverListener={!isFolded}
        enterDelay={300}
        placement="right"
        title={title}
      >
        {disabled ? (
          <Box component="span" sx={{ display: 'block', width: '100%' }}>
            {linkButton}
          </Box>
        ) : (
          linkButton
        )}
      </Tooltip>
    </li>
  )
}
